"use client"

import { Fragment, type ReactNode } from "react"
import Link from "next/link"
import Image from "next/image"
import { motion, useReducedMotion } from "framer-motion"
import {
  ArrowRight,
  ListChecks,
  RefreshCw,
  ShieldCheck,
  Sparkles,
  Workflow,
  Zap,
} from "lucide-react"
import { PageContainer, ConsultationCTA, type FaqItem } from "@/components/common"
import StructuredData from "@/components/common/StructuredData"
import { ServiceFaq } from "@/components/services"
import { CTAButton } from "@/components/ui/cta-button"
import { WhatsAppCta } from "@/components/shared/WhatsAppCta"
import { cn } from "@/lib/utils"
import { t } from "@/content/i18n/messages"
import { defaultLocale, getLocalizedPathname, type Locale } from "@/lib/i18n"
import GovernanceCallout from "@/components/ai-governance/GovernanceCallout";

// FAQ — co-located so the same data feeds the accordion UI and the FAQ JSON-LD.
// Server-rendered via <StructuredData type="faq"> so the FAQPage schema lands in
// the static export (see CLAUDE.md "FAQ exception"). Mirrors ClaudeManagedService.tsx.
//
// Stored in English because English doubles as the i18n lookup key; both consumers
// translate before rendering. ServiceFaq does it internally via t(); the JSON-LD
// has to be translated at the call site, or /ms and /zh ship a FAQPage whose
// questions do not match the ones on the page.
const aiSolutionsFaqs: Array<{ question: string; answer: string }> = [
{
    question: "What is an AI agency?",
    answer:
      "An AI agency designs, builds and runs AI systems for other businesses — as opposed to selling you a licence and leaving. In practice that means choosing the model, building the agent or assistant around your actual workflow, connecting it to the systems you already use, testing it against your data, and staying responsible for it after launch. Not to be confused with Malaysia's National AI Office (ai.gov.my), which is a government body, not a service provider.",
  },
{
    question: "What is the difference between an AI agency and a software development company?",
    answer:
      "A software house builds what you specify. An AI agency has to decide what is worth building, because AI systems fail differently: they are probabilistic, they drift as your data changes, and they need evaluation rather than just testing. The work is model selection, agent architecture, measurement before go-live, and monitoring afterwards. If a vendor quotes you an AI project the way they would quote a website, that is the warning sign.",
  },
  {
    question: "How much does it cost to hire an AI agency in Malaysia?",
    answer:
      "Cost depends on how many systems the agent touches, how sensitive the data is, and how much evaluation and monitoring the workflow needs. Scoped projects at Anchor Sprint run from RM 5,000 for a single automated workflow to RM 50,000 for an agent spanning several systems. Model running cost is separate and usually the smaller number; you can model it yourself with our Claude cost calculator. Every project is scoped and quoted before work starts.",
  },
  {
    question: "How long does an AI agent project take?",
    answer:
      "It depends on the workflow and how many systems are involved, so we agree the timeline when we scope the project rather than promise one up front. Every project runs through the same phases: discovery and scoping, a build against your real data, evaluation before launch, a controlled go-live with human approvals, then ongoing support and improvement. Claude onboarding and team training can run alongside the build.",
  },
  {
    question: "AI agency vs hiring in-house: which is better?",
    answer:
      "It depends on how much AI work you have and how soon you need results. Hiring in-house means recruiting scarce skills and building evaluation and monitoring practice from scratch. An AI agency brings that practice from the first project and stays accountable after launch. Many businesses start with an agency for the first workflows, then use training to build in-house capability so their own team can take ownership later.",
  },
  {
    question: "Do you work with businesses in Singapore too?",
    answer:
      "Yes. Anchor Sprint serves businesses in both Malaysia and Singapore. Anchor Sprint Pte. Ltd. delivers from Suntec in Singapore, and our engineering team is based in Shah Alam, Selangor. We quote in MYR in Malaysia and in SGD in Singapore.",
  },
  {
    question: "Is AI agency work HRD Corp claimable?",
    answer:
      "The training part is. Anchor Sprint is an HRD Corp approved training provider, so Claude and AI training for your employees can be claimed against your HRD Corp levy. Agent development and managed service fees are project costs, not training, so they fall outside the HRD Corp claim. HRD Corp is a Malaysian scheme, so the claim applies to Malaysian employers only.",
  },
{
    question: "What are the three main AI services you provide?",
    answer:
      "We provide AI Agent Development, Claude Managed Service, and Claude & AI Training. We build agents for real workflows, onboard and operate Claude across your organisation, and deliver structured training for employees and managers.",
  },
{
    question: "How do you handle data privacy and governance?",
    answer:
      "We design access, approval, and data-handling rules around the Claude plan and systems your organisation selects. The managed service documents ownership, user roles, approved workflows, and escalation points so employees know what they can use and when human review is required.",
  },
{
    question: "Why do you focus on Claude?",
    answer:
      "Claude is our primary platform for agent development and business adoption. Our team includes Claude Certified Architects and Anchor Sprint is Certified in the Claude Partner Network, so our services focus on building, onboarding, governing, and improving Claude-based work.",
  },
]

interface LocaleProps {
  locale: Locale
}

// Stable DOM id per question, so adding a FAQ can never shift or blank another's id.
const faqSlug = (question: string) =>
  question
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "")
    .slice(0, 60)

// Translates both fields so the FAQPage JSON-LD matches the visible accordion.
const localiseFaqs = (locale: Locale) =>
  aiSolutionsFaqs.map((faq) => ({
    question: t(locale, faq.question),
    answer: t(locale, faq.answer),
  }))

// Splits a translated headline on the [[...]] accent marker so translators can
// reorder words freely; the marked phrase renders with the emphasis underline.
function renderAccent(text: string) {
  const parts = text.split(/\[\[(.+?)\]\]/g)
  return parts.map((part, index) =>
    index % 2 === 1 ? (
      <span key={index} className="emphasis-underline">
        {part}
      </span>
    ) : (
      <Fragment key={index}>{part}</Fragment>
    ),
  )
}

// Hero section — light editorial treatment with framed dark media
function HeroSection({ locale }: LocaleProps) {
  const prefersReducedMotion = useReducedMotion()

  // Hero entrance moves only: opacity stays 1 so the hero paints before
  // hydration (LCP).
  const reveal = (delay: number) => ({
    initial: prefersReducedMotion ? false : { y: 12 },
    animate: { y: 0 },
    transition: { duration: 0.7, delay, ease: [0.21, 0.6, 0.35, 1] as const },
  })

  return (
    <section className="relative bg-background">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left content */}
          <div className="text-center lg:text-left space-y-8">
            <motion.p
              {...reveal(0)}
              className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted"
            >
              {/* Stops leading with "AI Agent Development": that belongs to
                  /services/ai-agents/, which is titled for it and owns the
                  keyword set. This page competed with its own sibling on the
                  eyebrow, the H1 and the title while neither ranked. */}
              {t(locale, "AI agency · Claude managed service · Claude training")}
            </motion.p>

            <motion.h1
              {...reveal(0.1)}
              className="font-display text-4xl sm:text-5xl lg:text-[3.5rem] font-bold leading-[1.08] tracking-tight text-neutral-emphasis"
            >
              {renderAccent(t(locale, "The AI agency Malaysian businesses [[hire]]"))}
            </motion.h1>

            <motion.p
              {...reveal(0.2)}
              className="text-lg lg:text-xl max-w-lg mx-auto lg:mx-0 text-neutral-muted leading-relaxed"
            >
              {t(
                locale,
                "We help Malaysian businesses in three clear ways: build AI agents, onboard and manage Claude across the team, and train employees to use AI responsibly.",
              )}
            </motion.p>

            {/* Key benefits */}
            <motion.div
              {...reveal(0.3)}
              className="flex flex-wrap justify-center lg:justify-start gap-x-6 gap-y-2 text-sm text-neutral-muted"
            >
              {["Claude Specialists", "Managed Adoption", "Local AI Experts"].map((benefit) => (
                <span key={benefit} className="flex items-center gap-2">
                  <svg className="w-4 h-4 text-primary" fill="currentColor" viewBox="0 0 20 20" aria-hidden><path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" /></svg>
                  {t(locale, benefit)}
                </span>
              ))}
            </motion.div>

            <motion.div
              {...reveal(0.4)}
              className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-4 justify-center lg:justify-start pt-2"
            >
              <CTAButton
                href="/contact/"
                size="large"
                className="w-full sm:w-auto rounded-full px-8"
              >
                Book an AI consultation
              </CTAButton>
              <WhatsAppCta
                message="Hi Anchor Sprint, I'd like to talk about an AI solution for my business (from your AI Solutions page)."
                location="ai_solutions_hero"
                label={t(locale, "WhatsApp us")}
                className="w-full sm:w-auto"
              />
              <Link
                href="#services"
                className="group inline-flex w-full items-center justify-center gap-2 text-base font-semibold text-neutral-emphasis transition hover:text-primary sm:w-auto"
              >
                {t(locale, "Explore solutions")}
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  aria-hidden
                />
              </Link>
            </motion.div>

            {/* Mobile AI tech showcase */}
            <motion.div {...reveal(0.5)} className="lg:hidden pt-4">
              <div className="flex flex-wrap justify-center gap-3">
                {["AI Agent Development", "Claude Managed Service", "Claude Training"].map((tech) => (
                  <div
                    key={tech}
                    className="px-4 py-2 rounded-full font-mono text-sm text-neutral-muted bg-card border border-border"
                  >
                    {t(locale, tech)}
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Right content — generated dark visual as framed media (desktop only via CSS) */}
          <motion.div
            {...reveal(0.25)}
            className="hidden lg:flex relative items-center justify-center"
          >
            <div className="w-full max-w-xl overflow-hidden rounded-2xl">
              <Image
                src="/images/services/ai-hub-editorial.jpg"
                alt={t(
                  locale,
                  "Editorial ink illustration of one path splitting into three AI service paths",
                )}
                width={768}
                height={512}
                priority
                className="h-auto w-full object-cover"
              />
            </div>
          </motion.div>
        </div>
      </div>
      <div className="h-px w-full bg-border" aria-hidden />
    </section>
  )
}

// Choose your path — routes each audience to its dedicated segment page
// Answer-first block: "What does an AI agency in Malaysia do?"
//
// Sits directly under the hero for extraction, not decoration. The SERP for
// "ai agency malaysia" carries an AI Overview and a local pack, and the agencies
// that rank answer the question in the first screen. So the H2 is the question,
// the paragraph under it is a self-contained 50-70 word answer, and the
// definition list repeats the facts as discrete key/value pairs (services, how we
// work, where, credentials) that an extractor can quote.
//
// Keyword boundary: this page owns "AI agency Malaysia". "AI agent development"
// belongs to /services/ai-agents/, so it appears here only as a link anchor.
// "Certified in the Claude Partner Network" is the approved Anthropic line and
// stays English in every locale (data-i18n-skip, never passed through t()).
function AgencyProfileSection({ locale }: LocaleProps) {
  const facts: Array<{ term: string; detail: ReactNode }> = [
    {
      term: t(locale, "Services"),
      detail: t(
        locale,
        "AI agent development, workflow automation, Claude deployment and managed operations, and HRD Corp claimable AI training.",
      ),
    },
    {
      term: t(locale, "How we work"),
      detail: t(
        locale,
        "Scope the workflow, build it, evaluate it against your data before launch, then support it in production. Every project is scoped and quoted before work starts, from RM 5,000 to RM 50,000.",
      ),
    },
    {
      // Market first, office second: the address still has to appear because
      // local pack eligibility and the Google Business Profile depend on it.
      term: t(locale, "Where we work"),
      detail: t(
        locale,
        "Malaysia and Singapore, with our engineering team in Shah Alam, Selangor and a Singapore office at Suntec.",
      ),
    },
    {
      term: t(locale, "Credentials"),
      detail: (
        <>
          <span data-i18n-skip>Certified in the Claude Partner Network.</span>{" "}
          {t(
            locale,
            "MDEC Malaysia Digital status. HRD Corp approved training provider. Claude Certified Architects on the team.",
          )}
        </>
      ),
    },
  ]

  // Link anchors are marked [[...]] inside one translatable sentence, so each
  // locale can reorder them; the nth marker maps to the nth href.
  const linkHrefs = ["/services/ai-agents/", "/solutions/agent-use-cases/"]
  const linkParts = t(
    locale,
    "Want the detail? See our [[AI agent development]] service, or browse [[agent use cases]] by workflow.",
  ).split(/\[\[(.+?)\]\]/g)

  return (
    <section
      aria-labelledby="ai-agency-answer"
      className="border-y border-border bg-neutral-surface py-16 lg:py-20"
    >
      <PageContainer maxWidth="6xl">
        <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
          {t(locale, "The short version")}
        </p>
        <h2
          id="ai-agency-answer"
          className="mt-4 max-w-3xl font-display text-3xl font-bold leading-tight tracking-tight text-neutral-emphasis sm:text-4xl"
        >
          {t(locale, "What does an AI agency in Malaysia do?")}
        </h2>
        <p className="mt-5 max-w-3xl font-display text-xl leading-relaxed text-neutral-emphasis lg:text-2xl">
          {t(
            locale,
            "As an AI agency in Malaysia, Anchor Sprint scopes the workflows worth automating, then builds and runs AI agents and automation on Claude, or on other models where they suit the work better. Every agent is evaluated against your real data before launch and supported after it goes live. We also train your staff to use AI well, with teams in Malaysia and Singapore.",
          )}
        </p>
        <p className="mt-4 max-w-3xl text-base leading-relaxed text-neutral-muted">
          {linkParts.map((part, index) =>
            index % 2 === 1 ? (
              <Link
                key={index}
                href={getLocalizedPathname(linkHrefs[(index - 1) / 2], locale)}
                className="font-semibold text-primary underline underline-offset-4 hover:text-primary-emphasis"
              >
                {part}
              </Link>
            ) : (
              <Fragment key={index}>{part}</Fragment>
            ),
          )}
        </p>

        <dl className="mt-10 grid gap-x-10 gap-y-6 sm:grid-cols-2">
          {facts.map((fact) => (
            <div key={fact.term}>
              <dt className="text-sm font-semibold uppercase tracking-wider text-neutral-muted">
                {fact.term}
              </dt>
              <dd className="mt-2 text-base leading-relaxed text-neutral-emphasis">
                {fact.detail}
              </dd>
            </div>
          ))}
        </dl>

        <p className="mt-8 text-sm text-neutral-muted">
          {t(
            locale,
            "Looking for Malaysia's National AI Office instead? That is a government body at ai.gov.my — it sets national AI policy and does not build systems for individual businesses.",
          )}
        </p>
      </PageContainer>
    </section>
  )
}

function ChooseYourPathSection({ locale }: LocaleProps) {
  const paths = [
    {
      icon: RefreshCw,
      title: t(locale, "Claude Managed Service"),
      promise: t(
        locale,
        "For organisations introducing Claude to employees: workspace setup, governance, onboarding, administration, and adoption support.",
      ),
      linkLabel: t(locale, "Explore Claude managed service"),
      href: "/services/claude-managed-service-malaysia/",
    },
    {
      icon: Workflow,
      title: t(locale, "AI Agent Development"),
      promise: t(
        locale,
        "For operations leaders: agents that take over invoicing, reporting, and repetitive workflows.",
      ),
      linkLabel: t(locale, "Explore AI agents"),
      href: "/services/ai-agents/",
    },
    {
      icon: ListChecks,
      title: t(locale, "Claude & AI Training"),
      promise: t(
        locale,
        "For business teams and managers: practical, HRD Corp claimable learning built around real work.",
      ),
      linkLabel: t(locale, "Explore Claude training"),
      href: "/services/training-education/",
    },
  ]

  return (
    <section id="services" className="scroll-mt-24 bg-background pb-8 pt-16 lg:pb-10 lg:pt-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Choose your path")}
          </p>
          <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-neutral-emphasis">
            {renderAccent(t(locale, "Start with the page built [[for your situation]]"))}
          </h2>
        </div>
        <div className="mt-10 grid gap-6 md:grid-cols-3">
          {paths.map((path) => (
            <Link
              key={path.href}
              href={getLocalizedPathname(path.href, locale)}
              className="group flex flex-col rounded-2xl border border-border bg-card p-6 transition-colors hover:border-primary/40"
            >
              <span className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
                <path.icon className="h-5 w-5 text-primary" aria-hidden />
              </span>
              <h3 className="mt-4 text-lg font-semibold text-neutral-emphasis">
                {path.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-neutral-muted">
                {path.promise}
              </p>
              <span className="mt-4 inline-flex items-center gap-2 pt-2 text-sm font-semibold text-primary">
                {path.linkLabel}
                <ArrowRight
                  className="h-4 w-4 transition-transform group-hover:translate-x-1"
                  aria-hidden
                />
              </span>
            </Link>
          ))}
        </div>
      </div>
    </section>
  )
}

// Trust bar with credibility stats
function TrustBar({ locale }: LocaleProps) {
  const stats = [
    { value: "15+", label: "Years Software Experience", icon: "M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" },
    { value: "10+", label: "Enterprise Solutions Delivered", icon: "M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" },
    { value: "MDEC", label: "Certified Company", icon: "M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" },
    { value: "Local", label: "Malaysian AI Team", icon: "M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z M15 11a3 3 0 11-6 0 3 3 0 016 0z" }
  ]

  return (
    <section className="relative py-6 lg:py-7 bg-background border-b border-border">
      <PageContainer maxWidth="6xl">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 lg:gap-8">
          {stats.map((stat) => (
            <div key={stat.label} className="flex items-center gap-3">
              <div className="w-11 h-11 lg:w-12 lg:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
                <svg className="w-5 h-5 lg:w-6 lg:h-6 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={stat.icon} />
                </svg>
              </div>
              <div>
                <div className="font-display text-xl lg:text-2xl font-bold text-neutral-emphasis leading-tight">{t(locale, stat.value)}</div>
                <div className="text-xs lg:text-sm text-neutral-muted">{t(locale, stat.label)}</div>
              </div>
            </div>
          ))}
        </div>
      </PageContainer>
    </section>
  )
}

// Agent runtime section - Plan -> Act -> Verify pipeline (dark proof moment)
function AgentRuntimeSection({ locale }: LocaleProps) {
  const prefersReducedMotion = useReducedMotion()

  const stages = [
    {
      step: "01",
      title: "Plan",
      icon: ListChecks,
      description:
        "The agent decomposes your goal into concrete steps — retrieving knowledge, selecting tools, and sequencing the work before anything runs.",
      points: ["Goal decomposition", "Tool selection", "Knowledge retrieval"],
    },
    {
      step: "02",
      title: "Act",
      icon: Zap,
      description:
        "It executes each step against your real systems — calling APIs, updating records, drafting documents, and moving work forward autonomously.",
      points: ["API & system calls", "Document drafting", "Workflow execution"],
    },
    {
      step: "03",
      title: "Verify",
      icon: ShieldCheck,
      description:
        "Every result is checked against guardrails and acceptance criteria. Anything that fails loops back into planning until the job is done.",
      points: ["Guardrail checks", "Result validation", "Self-correction loop"],
    },
  ]

  return (
    <section className="bg-background py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-12 max-w-3xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Agent runtime")}
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {renderAccent(t(locale, "How our agents get real work done. [[Plan. Act. Verify.]]"))}
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(
              locale,
              "Every agent we ship runs the same disciplined loop — so autonomy never means losing control of quality, security, or compliance.",
            )}
          </p>
        </div>

        {/* Dark proof moment: the agent pipeline */}
        <div className="rounded-3xl bg-hero-background p-5 sm:p-8 lg:p-10">
          <div className="flex flex-col items-stretch gap-2 lg:flex-row lg:items-stretch lg:gap-0">
            {stages.map(({ step, title, icon: Icon, description, points }, index) => (
              <Fragment key={step}>
                <motion.div
                  initial={prefersReducedMotion ? false : { opacity: 0, y: 28 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-80px" }}
                  transition={{
                    duration: 0.6,
                    delay: index * 0.15,
                    ease: [0.21, 0.6, 0.35, 1],
                  }}
                  className="flex flex-1 flex-col rounded-2xl border border-primary-contrast/[0.08] bg-primary-contrast/[0.03] p-7"
                >
                  <div className="mb-6 flex items-start justify-between">
                    <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-primary/10">
                      <Icon className="h-5 w-5 text-primary" aria-hidden />
                    </div>
                    <span className="font-mono text-sm text-primary-contrast/40">
                      {step}
                    </span>
                  </div>

                  <h3 className="mb-3 font-display text-2xl font-semibold text-primary-contrast">
                    {t(locale, title)}
                  </h3>
                  <p className="mb-6 flex-1 text-sm leading-relaxed text-primary-contrast/60">
                    {t(locale, description)}
                  </p>

                  <ul className="space-y-2">
                    {points.map((point) => (
                      <li
                        key={point}
                        className="flex items-center gap-2 font-mono text-xs text-primary-contrast/55"
                      >
                        <span className="h-1 w-1 rounded-full bg-primary" aria-hidden />
                        {t(locale, point)}
                      </li>
                    ))}
                  </ul>
                </motion.div>

                {/* Connector between nodes */}
                {index < stages.length - 1 && (
                  <motion.div
                    initial={prefersReducedMotion ? false : { opacity: 0, scale: 0.6 }}
                    whileInView={{ opacity: 1, scale: 1 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.5, delay: index * 0.15 + 0.25 }}
                    className="flex items-center justify-center"
                    aria-hidden
                  >
                    {/* Vertical connector on mobile */}
                    <div className="my-1 h-10 w-[2px] bg-primary/50 lg:hidden" />
                    {/* Horizontal connector on desktop */}
                    <div className="relative hidden h-[2px] w-10 bg-primary/50 lg:block xl:w-14">
                      <span className="absolute -right-1 top-1/2 h-2 w-2 -translate-y-1/2 rotate-45 border-r-2 border-t-2 border-primary" />
                    </div>
                  </motion.div>
                )}
              </Fragment>
            ))}
          </div>

          {/* Loop-back annotation */}
          <motion.div
            initial={prefersReducedMotion ? false : { opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.6, delay: 0.6 }}
            className="mt-10 flex justify-center"
          >
            <div className="inline-flex items-center gap-3 rounded-full border border-primary-contrast/[0.12] px-5 py-2.5">
              <RefreshCw className="h-4 w-4 text-primary" aria-hidden />
              <p className="text-sm text-primary-contrast/70">
                {t(locale, "Verification feeds back into planning — the loop runs until acceptance criteria pass.")}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  )
}

// Solution Categories Grid
function SolutionCategoriesSection({ locale }: LocaleProps) {
  const categories = [
    {
      name: "Agentic AI Development",
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
        </svg>
      ),
      description: "Build autonomous AI agents that can reason, plan, and execute complex multi-step tasks. From customer service to operations automation.",
      capabilities: ["AI Assistants & Chatbots", "Workflow Automation Agents", "Multi-Agent Systems", "Tool-Using AI Agents"],
      href: "/contact/"
    },
    {
      name: "Claude Managed Service",
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
      ),
      description: "Onboard Claude across your organisation and keep access, governance, adoption, administration, support, and reporting under control.",
      capabilities: ["Workspace Setup", "Team Onboarding", "Governance & Access", "Usage & Cost Reporting"],
      href: "/services/claude-managed-service-malaysia/",
      featured: true
    },
    {
      name: "RAG & Knowledge Systems",
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 6.042A8.967 8.967 0 006 3.75c-1.052 0-2.062.18-3 .512v14.25A8.987 8.987 0 016 18c2.305 0 4.408.867 6 2.292m0-14.25a8.966 8.966 0 016-2.292c1.052 0 2.062.18 3 .512v14.25A8.987 8.987 0 0018 18a8.967 8.967 0 00-6 2.292m0-14.25v14.25" />
        </svg>
      ),
      description: "Build intelligent knowledge management systems that understand your documents, policies, and data. Accurate answers grounded in your information.",
      capabilities: ["Document Q&A Systems", "Vector Database Integration", "Hybrid Search", "Source Citations"],
      href: "/contact/"
    },
    {
      name: "Intelligence Detection",
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M2.036 12.322a1.012 1.012 0 010-.639C3.423 7.51 7.36 4.5 12 4.5c4.638 0 8.573 3.007 9.963 7.178.07.207.07.431 0 .639C20.577 16.49 16.64 19.5 12 19.5c-4.638 0-8.573-3.007-9.963-7.178z" />
          <path strokeLinecap="round" strokeLinejoin="round" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      description: "AI-powered detection systems for fraud, anomalies, sentiment, and patterns. Turn your data into actionable intelligence.",
      capabilities: ["Fraud Detection", "Anomaly Detection", "Sentiment Analysis", "Pattern Recognition"],
      href: "/contact/"
    },
    {
      name: "Document Automation",
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 14.25v-2.625a3.375 3.375 0 00-3.375-3.375h-1.5A1.125 1.125 0 0113.5 7.125v-1.5a3.375 3.375 0 00-3.375-3.375H8.25m0 12.75h7.5m-7.5 3H12M10.5 2.25H5.625c-.621 0-1.125.504-1.125 1.125v17.25c0 .621.504 1.125 1.125 1.125h12.75c.621 0 1.125-.504 1.125-1.125V11.25a9 9 0 00-9-9z" />
        </svg>
      ),
      description: "Automate document creation, extraction, and processing. Generate contracts, reports, and proposals with AI assistance.",
      capabilities: ["Contract Generation", "Invoice Processing", "Report Automation", "Data Extraction"],
      href: "/contact/"
    },
    {
      name: "AI Integration Services",
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 16.875h3.375m0 0h3.375m-3.375 0V13.5m0 3.375v3.375M6 10.5h2.25a2.25 2.25 0 002.25-2.25V6a2.25 2.25 0 00-2.25-2.25H6A2.25 2.25 0 003.75 6v2.25A2.25 2.25 0 006 10.5zm0 9.75h2.25A2.25 2.25 0 0010.5 18v-2.25a2.25 2.25 0 00-2.25-2.25H6a2.25 2.25 0 00-2.25 2.25V18A2.25 2.25 0 006 20.25zm9.75-9.75H18a2.25 2.25 0 002.25-2.25V6A2.25 2.25 0 0018 3.75h-2.25A2.25 2.25 0 0013.5 6v2.25a2.25 2.25 0 002.25 2.25z" />
        </svg>
      ),
      description: "Integrate AI capabilities into your existing systems. Connect LLMs to your ERP, CRM, and business applications.",
      capabilities: ["API Development", "ERP Integration", "CRM AI Enhancement", "Legacy System Modernization"],
      href: "/contact/"
    }
  ]

  return (
    <section className="bg-neutral-surface py-24 lg:py-32 scroll-fade-in">
      <PageContainer maxWidth="6xl">
        <div className="mb-12 max-w-3xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "AI Services")}
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {renderAccent(t(locale, "AI Solutions for [[Every Need]]"))}
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(locale, "From conversational AI to enterprise automation — we have the expertise to build, deploy, and support your AI initiatives.")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {categories.map((category) => (
            <div
              key={category.name}
              className={cn(
                "relative rounded-2xl p-6 border bg-card transition-colors duration-200",
                category.featured
                  ? "border-primary/40"
                  : "border-border hover:border-neutral-border"
              )}
            >
              {category.featured && (
                <div className="absolute -top-3 right-6 z-10 px-3 py-1 bg-primary text-primary-foreground text-xs font-semibold rounded-full">
                  {t(locale, "Most Requested")}
                </div>
              )}

              <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-xl bg-primary/10 text-primary">
                {category.icon}
              </div>

              <h3 className="font-display text-lg font-bold text-neutral-emphasis mb-2">
                {t(locale, category.name)}
              </h3>
              <p className="text-sm text-neutral-muted mb-4">{t(locale, category.description)}</p>

              <div className="flex flex-wrap gap-2 mb-5">
                {category.capabilities.map((capability) => (
                  <span
                    key={capability}
                    className="px-2.5 py-1 text-xs text-neutral-muted rounded-full border border-border"
                  >
                    {t(locale, capability)}
                  </span>
                ))}
              </div>

              <Link
                href={category.href}
                className="inline-flex items-center gap-2 text-sm font-semibold text-primary hover:text-primary-emphasis transition-colors"
              >
                {t(locale, "Learn More")}
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </div>
          ))}
        </div>
      </PageContainer>
    </section>
  )
}

// Why choose us section - AI specific
function WhyChooseUsSection({ locale }: LocaleProps) {
  const benefits = [
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
        </svg>
      ),
      title: "Governed Claude Adoption",
      description: "We define ownership, access, approved workflows, and human approval points before Claude is rolled out across the organisation."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3.75 13.5l10.5-11.25L12 10.5h8.25L9.75 21.75 12 13.5H3.75z" />
        </svg>
      ),
      title: "Managed Operations",
      description: "Workspace administration, usage reporting, cost reviews, workflow updates, and adoption support continue after the initial rollout."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342" />
        </svg>
      ),
      title: "Claude-Focused Expertise",
      description: "Our team includes Claude Certified Architects and builds, onboards, and supports Claude-based workflows for Malaysian organisations."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
        </svg>
      ),
      title: "Local Malaysian Team",
      description: "No overseas support. Our AI engineers are based in Malaysia for responsive support, on-site deployment, and ongoing optimization."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M11.42 15.17L17.25 21A2.652 2.652 0 0021 17.25l-5.877-5.877M11.42 15.17l2.496-3.03c.317-.384.74-.626 1.208-.766M11.42 15.17l-4.655 5.653a2.548 2.548 0 11-3.586-3.586l6.837-5.63m5.108-.233c.55-.164 1.163-.188 1.743-.14a4.5 4.5 0 004.486-6.336l-3.276 3.277a3.004 3.004 0 01-2.25-2.25l3.276-3.276a4.5 4.5 0 00-6.336 4.486c.091 1.076-.071 2.264-.904 2.95l-.102.085m-1.745 1.437L5.909 7.5H4.5L2.25 3.75l1.5-1.5L7.5 4.5v1.409l4.26 4.26m-1.745 1.437l1.745-1.437m6.615 8.206L15.75 15.75M4.867 19.125h.008v.008h-.008v-.008z" />
        </svg>
      ),
      title: "End-to-End Delivery",
      description: "From workflow discovery and agent development to Claude onboarding, governance, training, and ongoing managed support."
    },
    {
      icon: (
        <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" />
        </svg>
      ),
      title: "Transparent Pricing",
      description: "Clear project and managed-service scopes, with platform usage reported separately so your organisation can track ongoing cost."
    }
  ]

  return (
    <section className="bg-background py-24 lg:py-32 scroll-fade-in">
      <PageContainer maxWidth="6xl">
        <div className="mb-12 max-w-3xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Why Choose Us")}
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {renderAccent(t(locale, "Malaysia's [[AI Integration]] Specialists"))}
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(locale, "We combine deep AI expertise with local presence to deliver enterprise solutions that work for Malaysian businesses.")}
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5 lg:gap-6">
          {benefits.map((benefit) => (
            <div
              key={benefit.title}
              className="rounded-2xl p-6 border border-border bg-card transition-colors duration-200 hover:border-neutral-border"
            >
              <div className="w-14 h-14 rounded-xl bg-primary/10 text-primary flex items-center justify-center mb-4">
                {benefit.icon}
              </div>
              <h3 className="font-display text-lg font-bold text-neutral-emphasis mb-2">{t(locale, benefit.title)}</h3>
              <p className="text-neutral-muted text-sm leading-relaxed">{t(locale, benefit.description)}</p>
            </div>
          ))}
        </div>
      </PageContainer>
    </section>
  )
}

// Industries served section
function IndustriesSection({ locale }: LocaleProps) {
  const industries = [
    { name: "Finance & Banking", icon: "M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00-.75.75v.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z" },
    { name: "Manufacturing", icon: "M8.25 21v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21m0 0h4.5V3.545M12.75 21h7.5V10.75M2.25 21h1.5m18 0h-18M2.25 9l4.5-1.636M18.75 3l-1.5.545m0 6.205l3 1m1.5.5l-1.5-.5M6.75 7.364V3h-3v18m3-13.636l10.5-3.819" },
    { name: "Retail & E-commerce", icon: "M2.25 3h1.386c.51 0 .955.343 1.087.835l.383 1.437M7.5 14.25a3 3 0 00-3 3h15.75m-12.75-3h11.218c1.121-2.3 2.1-4.684 2.924-7.138a60.114 60.114 0 00-16.536-1.84M7.5 14.25L5.106 5.272M6 20.25a.75.75 0 11-1.5 0 .75.75 0 011.5 0zm12.75 0a.75.75 0 11-1.5 0 .75.75 0 011.5 0z" },
    { name: "Legal & Professional", icon: "M12 3v17.25m0 0c-1.472 0-2.882.265-4.185.75M12 20.25c1.472 0 2.882.265 4.185.75M18.75 4.97A48.416 48.416 0 0012 4.5c-2.291 0-4.545.16-6.75.47m13.5 0c1.01.143 2.01.317 3 .52m-3-.52l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.988 5.988 0 01-2.031.352 5.988 5.988 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L18.75 4.971zm-16.5.52c.99-.203 1.99-.377 3-.52m0 0l2.62 10.726c.122.499-.106 1.028-.589 1.202a5.989 5.989 0 01-2.031.352 5.989 5.989 0 01-2.031-.352c-.483-.174-.711-.703-.59-1.202L5.25 4.971z" },
    { name: "Healthcare", icon: "M21 8.25c0-2.485-2.099-4.5-4.688-4.5-1.935 0-3.597 1.126-4.312 2.733-.715-1.607-2.377-2.733-4.313-2.733C5.1 3.75 3 5.765 3 8.25c0 7.22 9 12 9 12s9-4.78 9-12z" },
    { name: "Government & GLC", icon: "M12 21v-8.25M15.75 21v-8.25M8.25 21v-8.25M3 9l9-6 9 6m-1.5 12V10.332A48.36 48.36 0 0012 9.75c-2.551 0-5.056.2-7.5.582V21M3 21h18M12 6.75h.008v.008H12V6.75z" },
    { name: "Education", icon: "M4.26 10.147a60.436 60.436 0 00-.491 6.347A48.627 48.627 0 0112 20.904a48.627 48.627 0 018.232-4.41 60.46 60.46 0 00-.491-6.347m-15.482 0a50.57 50.57 0 00-2.658-.813A59.905 59.905 0 0112 3.493a59.902 59.902 0 0110.399 5.84c-.896.248-1.783.52-2.658.814m-15.482 0A50.697 50.697 0 0112 13.489a50.702 50.702 0 017.74-3.342M6.75 15a.75.75 0 100-1.5.75.75 0 000 1.5zm0 0v-3.675A55.378 55.378 0 0112 8.443m-7.007 11.55A5.981 5.981 0 006.75 15.75v-1.5" },
    { name: "Logistics & Supply Chain", icon: "M8.25 18.75a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h6m-9 0H3.375a1.125 1.125 0 01-1.125-1.125V14.25m17.25 4.5a1.5 1.5 0 01-3 0m3 0a1.5 1.5 0 00-3 0m3 0h1.125c.621 0 1.129-.504 1.09-1.124a17.902 17.902 0 00-3.213-9.193 2.056 2.056 0 00-1.58-.86H14.25M16.5 18.75h-2.25m0-11.177v-.958c0-.568-.422-1.048-.987-1.106a48.554 48.554 0 00-10.026 0 1.106 1.106 0 00-.987 1.106v7.635m12-6.677v6.677m0 4.5v-4.5m0 0h-12" }
  ]

  return (
    <section className="bg-neutral-surface py-24 lg:py-32">
      <PageContainer maxWidth="6xl">
        <div className="mb-12 max-w-3xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "Industries")}
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {renderAccent(t(locale, "AI Solutions [[Across Industries]]"))}
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(locale, "We've deployed AI solutions across diverse sectors, each with unique requirements and compliance needs.")}
          </p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 lg:gap-5">
          {industries.map((industry) => (
            <div
              key={industry.name}
              className="rounded-xl p-5 lg:p-6 border border-border bg-card text-center transition-colors duration-200 hover:border-neutral-border"
            >
              <div className="w-14 h-14 lg:w-16 lg:h-16 mx-auto mb-3 lg:mb-4 rounded-xl flex items-center justify-center bg-primary/10 text-primary">
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d={industry.icon} />
                </svg>
              </div>
              <h3 className="text-sm lg:text-base font-semibold text-neutral-emphasis">{t(locale, industry.name)}</h3>
            </div>
          ))}
        </div>
      </PageContainer>
    </section>
  )
}

// FAQ Section - light accordion with hairline dividers.
// Shares aiSolutionsFaqs with the server-rendered <StructuredData type="faq"> in
// the main component, so the visible accordion and the FAQPage JSON-LD never drift.
// Ids are derived from the question rather than hand-listed — a fixed id array
// silently went short when questions were added, leaving later rows undefined.
function FAQSection({ locale }: LocaleProps) {
  const faqs: FaqItem[] = aiSolutionsFaqs.map((faq) => ({
    id: faqSlug(faq.question),
    question: faq.question,
    answer: faq.answer,
  }))

  return (
    <section className="bg-background py-24 lg:py-32">
      <PageContainer maxWidth="4xl">
        <div className="mb-12 max-w-3xl">
          <p className="mb-3 text-sm font-semibold uppercase tracking-[0.3em] text-neutral-muted">
            {t(locale, "FAQ")}
          </p>
          <h2 className="font-display text-3xl font-bold leading-tight text-neutral-emphasis md:text-5xl">
            {renderAccent(t(locale, "Frequently Asked [[Questions]]"))}
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-neutral-muted">
            {t(locale, "Common questions about our AI solutions and deployment approach.")}
          </p>
        </div>

        <ServiceFaq items={faqs} />
      </PageContainer>
    </section>
  )
}

interface AISolutionsProps {
  locale?: Locale
}

// Main component
export default function AISolutions({ locale = defaultLocale }: AISolutionsProps) {
  return (
    <>
      {/* FAQ JSON-LD — server-rendered so the FAQPage schema lands in the static
          export. Organization/WebSite/ProfessionalService come from the global
          layout, so no Organization block is needed here. */}
      <StructuredData type="faq" faqs={localiseFaqs(locale)} />

      <div className="bg-background text-neutral-emphasis">
        <HeroSection locale={locale} />
        <AgencyProfileSection locale={locale} />
        <ChooseYourPathSection locale={locale} />
        <TrustBar locale={locale} />
        <AgentRuntimeSection locale={locale} />
        <WhyChooseUsSection locale={locale} />
        <IndustriesSection locale={locale} />
        <section className="bg-background px-4 py-12 sm:px-6 lg:px-8">
          <GovernanceCallout locale={locale} variant="rollout" placement="ai_solutions" />
        </section>
        <FAQSection locale={locale} />
        {/* Closing CTA */}
            <ConsultationCTA
              title="Which AI service should you start with?"
              description="Tell us the business outcome you want. We will recommend whether to begin with AI agent development, Claude managed onboarding, or practical team training."
              buttonText="Book an AI consultation"
              whatsapp={{
                message: "Hi Anchor Sprint, I'd like to talk about an AI solution for my business (from your AI Solutions page).",
                location: "ai_solutions_closing_cta",
              }}
            />
      </div>
    </>
  )
}
